import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert } from 'react-bootstrap';
import { FaWhatsapp, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const WhatsappManagement = () => {
  const { token } = useAuth();
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch all WhatsApp numbers
  const fetchWhatsappNumbers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://realestate.bonlineco.com/api/admin/whatsapp`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setWhatsappNumbers(response.data.data);
      }
    } catch (error) {
      setError('Failed to fetch WhatsApp numbers');
      console.error('Error fetching WhatsApp numbers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWhatsappNumbers();
  }, [token]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      let response;
      
      if (editingId) {
        // Update existing number
        response = await axios.put(
          `https://realestate.bonlineco.com/api/admin/whatsapp/${editingId}`,
          { phone_number: phoneNumber, is_active: isActive },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSuccess('WhatsApp number updated successfully');
      } else {
        // Create new number
        response = await axios.post(
          `https://realestate.bonlineco.com/api/admin/whatsapp`,
          { phone_number: phoneNumber, is_active: isActive },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setSuccess('WhatsApp number added successfully');
      }
      
      // Reset form
      setPhoneNumber('');
      setIsActive(true);
      setEditingId(null);
      
      // Refresh the list
      fetchWhatsappNumbers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save WhatsApp number');
      console.error('Error saving WhatsApp number:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit button click
  const handleEdit = (whatsapp) => {
    setPhoneNumber(whatsapp.phone_number);
    setIsActive(whatsapp.is_active);
    setEditingId(whatsapp.id);
  };

  // Handle toggle active status
  const handleToggleActive = async (id) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.put(
        `https://realestate.bonlineco.com/api/admin/whatsapp/${id}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('WhatsApp number status updated successfully');
      fetchWhatsappNumbers();
    } catch (error) {
      setError('Failed to update WhatsApp number status');
      console.error('Error updating WhatsApp number status:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete button click
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this WhatsApp number?')) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await axios.delete(
        `https://realestate.bonlineco.com/api/admin/whatsapp/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('WhatsApp number deleted successfully');
      fetchWhatsappNumbers();
    } catch (error) {
      setError('Failed to delete WhatsApp number');
      console.error('Error deleting WhatsApp number:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleCancel = () => {
    setPhoneNumber('');
    setIsActive(true);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  return (
    <Container fluid className="admin-content-container">
      <Row className="mb-4">
        <Col>
          <h2 className="admin-section-title">
            <FaWhatsapp className="me-2" />
            WhatsApp Management
          </h2>
          <p className="admin-section-description">
            Manage the WhatsApp number displayed on the website. Only one number can be active at a time.
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <Row className="mb-4">
        <Col md={6}>
          <Card className="admin-card">
            <Card.Header className="admin-card-header">
              {editingId ? 'Edit WhatsApp Number' : 'Add New WhatsApp Number'}
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter WhatsApp number (e.g., 0540322920)"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter the number without country code or special characters
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Set as active WhatsApp number"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    Only one number can be active at a time. Setting this as active will deactivate all other numbers.
                  </Form.Text>
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? 'Saving...' : editingId ? 'Update Number' : 'Add Number'}
                  </Button>
                  {editingId && (
                    <Button variant="secondary" onClick={handleCancel}>
                      Cancel
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="admin-card">
            <Card.Header className="admin-card-header">
              WhatsApp Numbers
            </Card.Header>
            <Card.Body>
              {whatsappNumbers.length === 0 ? (
                <p className="text-center py-3">No WhatsApp numbers found</p>
              ) : (
                <Table responsive hover className="admin-table">
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {whatsappNumbers.map((whatsapp) => (
                      <tr key={whatsapp.id}>
                        <td>{whatsapp.phone_number}</td>
                        <td>
                          {whatsapp.is_active ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-secondary">Inactive</span>
                          )}
                        </td>
                        <td>{new Date(whatsapp.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button 
                              variant="outline-primary" 
                              size="sm" 
                              onClick={() => handleEdit(whatsapp)}
                              title="Edit"
                            >
                              <FaEdit />
                            </Button>
                            <Button 
                              variant={whatsapp.is_active ? "outline-secondary" : "outline-success"} 
                              size="sm" 
                              onClick={() => handleToggleActive(whatsapp.id)}
                              title={whatsapp.is_active ? "Deactivate" : "Activate"}
                            >
                              {whatsapp.is_active ? <FaTimes /> : <FaCheck />}
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm" 
                              onClick={() => handleDelete(whatsapp.id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default WhatsappManagement;
